import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent
} from "react";
import { Link } from "@tanstack/react-router";
import { BarChart3, RotateCcw, Table2, ZoomIn } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { ExperimentProjection, RunProjection } from "@roboscientist/contracts";
import { EmptyState, OverflowReveal, StatusPill } from "@roboscientist/ui";
import { useThemePalette } from "../useThemePalette.js";
import { Chart } from "./Chart.js";
import { RunReplayChart } from "./RunReplayChart.js";
import type { ReplayClock } from "./replayClock.js";
import { GANTT_AXIS_HEIGHT, GANTT_ROW_HEIGHT, ganttContentHeight } from "./chartMath.js";
import {
  progressUnitLabel,
  progressValue,
  runningRunDecal,
  timelineBarGeometry,
  timelineTimeAtPosition
} from "./runPresentation.js";
import { isRunningRun, runDuration, runEnd, runStart } from "./runTiming.js";
import {
  createBrokenTimelineScale,
  type BrokenTimelineScale,
  type TimelineBreak
} from "./timelineBreaks.js";

type Range = "24h" | "7d" | "all";
type View = "gantt" | "table";
type RunScope = "focus" | "all";
type RunColorPalette = "theme" | "classic";
type CursorAlignment = "start" | "center" | "end";
type TimelineCursor = { x: number; time: number; alignment: CursorAlignment; gap: TimelineBreak | null };
type TimelineZoomGesture = {
  pointerId: number;
  mode: "zoom" | "pan";
  startX: number;
  currentX: number;
  scale: BrokenTimelineScale;
  moved: boolean;
};
type TimelineZoomSelection = { x0: number; x1: number };

const GANTT_RIGHT_INSET = 18;

const isExceptional = (run: RunProjection) => /active|running|fail|error|interrupt/i.test(run.status);
const isRoutineCheck = (run: RunProjection) => /(?:^|_)(?:smoke|diag|audit|profile|validate)(?:_|$)/i.test(`${runName(run)} ${run.path}`);
export const isFocusRun = (run: RunProjection, now: number) => isExceptional(run) || (runDuration(run, now) >= 120_000 && !isRoutineCheck(run));
const formatDuration = (milliseconds: number) => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};
const formatTimelineTime = (timestamp: number) => new Date(timestamp).toLocaleString("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
});
const formatTimelineTick = (timestamp: number) => new Date(timestamp).toLocaleString("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});

const runName = (run: RunProjection) => run.id.split(":").at(-1) ?? run.id;
const runDisplayEnd = (run: RunProjection, now: number) => Math.max(runStart(run) + 1_000, runEnd(run, now));
const statusTone = (status: string) => status === "active" || status === "running" ? "live" : status === "completed" ? "accent" : status.includes("fail") || status.includes("error") ? "danger" : "warning";
const semanticLabel = (run: RunProjection) =>
  run.semanticProvenance.epistemicClass === "observed_fact"
    ? "Harness fact"
    : run.kind === "unknown" ? "Unknown" : "Inferred";
const classicKindColors: Record<RunProjection["kind"], string> = {
  training: "#38c98d",
  evaluation: "#9b6de3",
  diagnostic: "#e39a36",
  unknown: "#6f86a6"
};
const kindColor = (
  kind: RunProjection["kind"],
  colors: { success: string; done: string; attention: string; muted: string },
  colorPalette: RunColorPalette
) => colorPalette === "classic"
  ? classicKindColors[kind]
  : kind === "training"
    ? colors.success
    : kind === "evaluation"
      ? colors.done
      : kind === "diagnostic"
        ? colors.attention
        : colors.muted;
const runKinds: Array<{ kind: RunProjection["kind"]; label: string }> = [
  { kind: "training", label: "Training" },
  { kind: "evaluation", label: "Evaluation" },
  { kind: "diagnostic", label: "Diagnostic" }
];

export type RunReplayPresentation = {
  clock?: ReplayClock;
  scope?: RunScope;
  instant?: boolean;
  windowMs?: number;
  cameraTimes?: readonly number[];
  cursorTime: number;
  observedAt: number;
  visibleRunIds: ReadonlySet<string>;
  activeRunIds: ReadonlySet<string>;
  runDetails: ReadonlyMap<string, RunProjection>;
  complete: boolean;
  playing: boolean;
  onInspect: () => void;
  onRunsChange?: (runs: RunProjection[]) => void;
};

export function RunExplorer({ experiments, runs, selectedRunId, onSelectRun, emptyDetail = "No experiment is confirmed for this Research Topic. Suggested links remain reviewable instead of being treated as fact.", readOnly = false, colorPalette = "theme", replay: replayInput }: {
  experiments: ExperimentProjection[];
  runs: RunProjection[];
  selectedRunId: string | null;
  onSelectRun?: (id: string) => void;
  emptyDetail?: string;
  readOnly?: boolean;
  colorPalette?: RunColorPalette;
  replay?: RunReplayPresentation;
}) {
  const replay = readOnly ? replayInput : undefined;
  const { isLight, palette } = useThemePalette();
  const [view, setView] = useState<View>("gantt");
  const [range, setRange] = useState<Range>("all");
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [localRunScope, setRunScope] = useState<RunScope>(replay ? "all" : "focus");
  const runScope = replay?.scope ?? localRunScope;
  const [zoomWindow, setZoomWindow] = useState({ start: 0, end: 100 });
  const [isPanning, setIsPanning] = useState(false);
  const [gapCompressionEnabled, setGapCompressionEnabled] = useState(true);
  const chartShellRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const zoomGestureRef = useRef<TimelineZoomGesture | null>(null);
  const suppressChartClickRef = useRef(false);
  const [plotWidth, setPlotWidth] = useState(620 - GANTT_RIGHT_INSET);
  const [inspectedRunId, setInspectedRunId] = useState<string | null>(null);
  const [chartHover, setChartHover] = useState<{ runId: string; x: number; y: number } | null>(null);
  const [timelineCursor, setTimelineCursor] = useState<TimelineCursor | null>(null);
  const [zoomSelection, setZoomSelection] = useState<TimelineZoomSelection | null>(null);
  useEffect(() => {
    if (!replay) return;
    setInspectedRunId(null);
    setChartHover(null);
  }, [selectedRunId, Boolean(replay)]);
  const hasRunningRuns = runs.some(isRunningRun);
  const [liveNow, setNow] = useState(() => Date.now());
  const now = replay?.observedAt ?? liveNow;
  useEffect(() => {
    if (!hasRunningRuns || replay) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [hasRunningRuns, Boolean(replay)]);
  const experimentById = useMemo(() => new Map(experiments.map((experiment) => [experiment.id, experiment])), [experiments]);
  const filtered = useMemo(() => {
    const referenceTime = replay?.observedAt ?? Date.now();
    const cutoff = range === "24h" ? referenceTime - 86_400_000 : range === "7d" ? referenceTime - 604_800_000 : 0;
    const needle = search.trim().toLowerCase();
    return [...runs].filter((run) => {
      const experiment = experimentById.get(run.experimentId);
      const haystack = `${run.id} ${run.path} ${run.experimentId} ${experiment?.name ?? ""}`.toLowerCase();
      return (!needle || haystack.includes(needle))
        && (kind === "all" || run.kind === kind)
        && (status === "all" || run.status === status)
        && (!cutoff || Math.max(runEnd(run, now), runStart(run)) >= cutoff);
    }).sort((a, b) => runStart(a) - runStart(b));
  }, [experimentById, kind, now, range, runs, search, status]);
  const focusRuns = useMemo(() => filtered.filter((run) => isFocusRun(run, now)), [filtered, now]);
  const visible = runScope === "focus" ? focusRuns : filtered;
  const onReplayRunsChange = replay?.onRunsChange;
  useEffect(() => {
    onReplayRunsChange?.(visible);
  }, [onReplayRunsChange, visible]);
  useEffect(() => {
    if (!replay) return;
    setInspectedRunId(null);
    setChartHover(null);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [visible, Boolean(replay)]);
  const timelineBounds = useMemo(() => {
    const minimum = visible.length ? Math.min(...visible.map(runStart)) : now;
    const latest = visible.length ? Math.max(...visible.map((run) => runDisplayEnd(run, now))) : minimum + 1_000;
    return { minimum, maximum: Math.max(minimum + 1_000, latest) };
  }, [now, visible]);
  useEffect(() => {
    const element = chartShellRef.current;
    if (!element || view !== "gantt" || !visible.length) return;
    const updateWidth = () => setPlotWidth(Math.max(1, element.getBoundingClientRect().width - GANTT_RIGHT_INSET));
    updateWidth();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [view, visible.length]);
  const visibleDomain = useMemo(() => {
    if (replay?.windowMs) {
      const span = Math.min(replay.windowMs, timelineBounds.maximum - timelineBounds.minimum);
      const start = Math.max(timelineBounds.minimum, Math.min(timelineBounds.maximum - span, (replay.cameraTimes?.length ? replay.cameraTimes.reduce((sum, time) => sum + time, 0) / replay.cameraTimes.length : replay.cursorTime) - span * .67));
      return { start, end: start + span };
    }
    return ({
    start: timelineTimeAtPosition({
      ...timelineBounds,
      zoomStart: zoomWindow.start,
      zoomEnd: zoomWindow.end,
      position: 0,
      width: 1
    }),
    end: timelineTimeAtPosition({
      ...timelineBounds,
      zoomStart: zoomWindow.start,
      zoomEnd: zoomWindow.end,
      position: 1,
      width: 1
    })
  }); }, [timelineBounds, zoomWindow.end, zoomWindow.start, replay?.windowMs, replay?.cursorTime, replay?.cameraTimes]);
  const timelineIntervals = useMemo(() => visible.map((run) => ({
    start: runStart(run),
    end: runDisplayEnd(run, now)
  })), [now, visible]);
  const compressedTimeScale = useMemo(() => createBrokenTimelineScale({
    intervals: timelineIntervals,
    domainStart: visibleDomain.start,
    domainEnd: visibleDomain.end,
    width: plotWidth
  }), [plotWidth, timelineIntervals, visibleDomain.end, visibleDomain.start]);
  const timeScale = useMemo(() => gapCompressionEnabled
    ? compressedTimeScale
    : createBrokenTimelineScale({
      intervals: timelineIntervals,
      domainStart: visibleDomain.start,
      domainEnd: visibleDomain.end,
      width: plotWidth,
      maximumBreakRatio: 0
    }), [compressedTimeScale, gapCompressionEnabled, plotWidth, timelineIntervals, visibleDomain.end, visibleDomain.start]);
  const timelineTicks = useMemo(() => Array.from({ length: 4 }, (_, index) => {
    const position = timeScale.width * index / 3;
    if (index > 0 && index < 3 && timeScale.breakAtX(position)) return null;
    return {
      alignment: index === 0 ? "start" : index === 3 ? "end" : "center",
      position,
      time: timeScale.fromX(position)
    };
  }).filter((tick): tick is NonNullable<typeof tick> => tick != null), [timeScale]);
  const collapsedCount = filtered.length - focusRuns.length;
  const inspectableRuns = replay ? visible.filter((run) => replay.visibleRunIds.has(run.id)) : runs;
  const inspectedSource = (replay?.playing ? null : inspectableRuns.find((run) => run.id === inspectedRunId)) ?? inspectableRuns.find((run) => run.id === selectedRunId) ?? null;
  const inspectedRun = inspectedSource ? replay?.runDetails.get(inspectedSource.id) ?? inspectedSource : null;
  const replayActive = (run: RunProjection) => replay?.activeRunIds.has(run.id) ?? false;
  const displayedDuration = (run: RunProjection) => replayActive(run) ? Math.max(0, replay!.cursorTime - runStart(run)) : runDuration(run, now);
  const activeRow = replay && !replay.complete ? visible.reduce((latest, run, index) => replay.activeRunIds.has(run.id) ? index : latest, -1) : -1;
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll || activeRow < 0 || replay?.instant || replay?.clock) return;
    const top = activeRow * GANTT_ROW_HEIGHT;
    if (top < scroll.scrollTop || top + GANTT_ROW_HEIGHT > scroll.scrollTop + scroll.clientHeight - GANTT_AXIS_HEIGHT) {
      scroll.scrollTo({ top: Math.max(0, top - scroll.clientHeight * .4), behavior: replay?.instant ? "auto" : "smooth" });
    }
  }, [activeRow]);
  useEffect(() => {
    if (!replay?.instant || !scrollRef.current) return;
    const rowAt = (time: number) => {
      const index = visible.reduce((found, run, i) => runStart(run) <= time ? i : found, -1);
      if (index < 0) return 0;
      const start = runStart(visible[index]!);
      const next = visible[index + 1] ? runStart(visible[index + 1]!) : start + 1;
      const fraction = Math.max(0, Math.min(1, (time - start) / Math.max(1, next - start)));
      return Math.max(0, index + fraction - 8);
    };
    const samples = replay.cameraTimes?.length ? replay.cameraTimes : [replay.cursorTime];
    scrollRef.current.scrollTop = samples.reduce((sum, time) => sum + rowAt(time), 0) / samples.length * GANTT_ROW_HEIGHT;
  }, [replay?.instant, replay?.cursorTime, replay?.cameraTimes, visible]);
  useLayoutEffect(() => {
    const scroll = scrollRef.current, clock = replay?.clock;
    if (!scroll || !clock || view !== "gantt") return;
    let target = scroll.scrollTop, previous = performance.now(), previousTime = clock.getTime();
    const move = (time: number) => {
      const now = performance.now(), delta = Math.min(64, now - previous);
      previous = now;
      const row = visible.reduce((found, run, index) => runStart(run) <= time ? index : found, -1);
      const top = Math.max(0, row * GANTT_ROW_HEIGHT);
      if (time < previousTime || !replay.playing) {
        // A seek is explicit; land on the selected part of history immediately.
        target = Math.max(0, top - scroll.clientHeight * .4);
        scroll.scrollTop = target;
      } else {
        if (top < target || top + GANTT_ROW_HEIGHT > target + scroll.clientHeight - GANTT_AXIS_HEIGHT) {
          target = Math.max(0, top - scroll.clientHeight * .4);
        }
        const distance = target - scroll.scrollTop;
        if (Math.abs(distance) > .5) scroll.scrollTop += distance * (1 - Math.exp(-delta / 120));
      }
      previousTime = time;
    };
    return clock.subscribe(move);
  }, [replay?.clock, replay?.playing, visible, view]);
  const replayColors = useMemo(() => Object.fromEntries(
    (["training", "evaluation", "diagnostic", "unknown"] as const).map((kind) => [kind, kindColor(kind, palette, colorPalette)])
  ) as Record<RunProjection["kind"], string>, [palette, colorPalette]);
  const hoveredRun = chartHover ? visible.find((run) => run.id === chartHover.runId) ?? null : null;
  const resetZoom = () => {
    const reset = { start: 0, end: 100 };
    setZoomWindow(reset);
  };
  const setVisibleDomain = (requestedStart: number, requestedEnd: number) => {
    const totalDuration = Math.max(1, timelineBounds.maximum - timelineBounds.minimum);
    const minimumSpan = Math.max(1_000, totalDuration * 0.01);
    let span = Math.max(minimumSpan, requestedEnd - requestedStart);
    if (span >= totalDuration) {
      resetZoom();
      return;
    }
    let start = requestedStart;
    let end = start + span;
    if (start < timelineBounds.minimum) {
      start = timelineBounds.minimum;
      end = start + span;
    }
    if (end > timelineBounds.maximum) {
      end = timelineBounds.maximum;
      start = end - span;
    }
    start = Math.max(timelineBounds.minimum, start);
    end = Math.min(timelineBounds.maximum, end);
    span = Math.max(1, end - start);
    const nextZoom = {
      start: (start - timelineBounds.minimum) / totalDuration * 100,
      end: (start + span - timelineBounds.minimum) / totalDuration * 100
    };
    setChartHover(null);
    setZoomWindow((current) =>
      Math.abs(current.start - nextZoom.start) < 0.0001 && Math.abs(current.end - nextZoom.end) < 0.0001
        ? current
        : nextZoom
    );
  };
  const option = useMemo<EChartsOption>(() => {
    if (replay?.clock) return {};
    const labels = visible.map((run) => runName(run));
    const bars = visible.map((run) => timelineBarGeometry(
      timeScale, runStart(run), runDisplayEnd(run, now),
      replay && !replay.complete ? replay.cursorTime : undefined
    ));
    const starts = bars.map((bar) => bar.start);
    const durations = visible.map((run, index) => {
      const color = kindColor(run.kind, palette, colorPalette);
      const displayDuration = bars[index]!.width;
      const isVisible = displayDuration > 0;
      return { value: displayDuration, itemStyle: {
        color, borderRadius: 2, opacity: isVisible ? (isExceptional(run) || replayActive(run) ? 1 : .9) : 0, shadowBlur: isVisible ? (replayActive(run) ? 16 : 7) : 0, shadowColor: `${color}66`,
        decal: runningRunDecal(
          replayActive(run) ? { ...run, status: "running" } : run,
          colorPalette === "classic"
            ? isLight ? "rgba(14, 29, 48, .30)" : "rgba(255, 255, 255, .36)"
            : palette.neutralMuted
        )
      } };
    });
    return {
      animation: false,
      grid: { left: 0, right: GANTT_RIGHT_INSET, top: 0, bottom: GANTT_AXIS_HEIGHT },
      tooltip: { show: false },
      xAxis: {
        type: "value", min: 0, max: timeScale.width,
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: colorPalette === "classic"
          ? isLight ? "rgba(35,55,78,.10)" : "rgba(150,170,200,.12)"
          : palette.borderMuted } },
        axisLine: { show: false }
      },
      yAxis: { type: "category", data: labels, inverse: true, axisLabel: { show: false }, axisTick: { show: false }, axisLine: { show: false } },
      series: [
        { type: "bar", stack: "runs", silent: true, data: starts, itemStyle: { color: "transparent" }, emphasis: { disabled: true }, barWidth: 14 },
        { type: "bar", name: "Run duration", stack: "runs", data: durations, barWidth: 14, emphasis: { itemStyle: { opacity: 1, shadowBlur: 14 } } }
      ]
    };
  }, [colorPalette, isLight, now, palette, timeScale, visible, visibleDomain.end, visibleDomain.start, replay]);
  const trackTimelineCursor = (clientX: number, element: HTMLDivElement) => {
    const bounds = element.getBoundingClientRect();
    const plotWidth = Math.max(1, bounds.width - GANTT_RIGHT_INSET);
    const x = Math.max(0, Math.min(plotWidth, clientX - bounds.left));
    const time = timeScale.fromX(x);
    const gap = timeScale.breakAtX(x);
    const alignment: CursorAlignment = x < 74 ? "start" : x > plotWidth - 74 ? "end" : "center";
    setTimelineCursor((current) => current?.x === x && current.time === time && current.gap === gap ? current : { x, time, alignment, gap });
  };
  const handleTimelinePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.button !== 2) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const startX = Math.max(0, Math.min(timeScale.width, event.clientX - bounds.left));
    const mode = event.button === 2 ? "pan" : "zoom";
    zoomGestureRef.current = {
      pointerId: event.pointerId,
      mode,
      startX,
      currentX: startX,
      scale: timeScale,
      moved: false
    };
    setZoomSelection(null);
    if (mode === "pan") {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
      setChartHover(null);
      setTimelineCursor(null);
    }
  };
  const handleTimelineTrackpadPan = (event: ReactWheelEvent<HTMLDivElement>) => {
    const zoomed = zoomWindow.start > 0.0001 || zoomWindow.end < 99.9999;
    const horizontalDelta = event.deltaX;
    if (
      !zoomed
      || zoomGestureRef.current
      || Math.abs(horizontalDelta) < 2
      || Math.abs(horizontalDelta) <= Math.abs(event.deltaY)
    ) return;

    event.preventDefault();
    event.stopPropagation();
    const deltaInPixels = event.deltaMode === 1
      ? horizontalDelta * 16
      : event.deltaMode === 2
        ? horizontalDelta * timeScale.width
        : horizontalDelta;
    const boundedPixelDelta = Math.max(
      -timeScale.width * 0.25,
      Math.min(timeScale.width * 0.25, deltaInPixels)
    );
    const timeDelta = boundedPixelDelta / Math.max(Number.EPSILON, timeScale.pixelsPerMillisecond);
    setVisibleDomain(visibleDomain.start + timeDelta, visibleDomain.end + timeDelta);
    setChartHover(null);
    setTimelineCursor(null);
  };
  const handleTimelinePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    trackTimelineCursor(event.clientX, event.currentTarget);
    const gesture = zoomGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const currentX = Math.max(0, Math.min(gesture.scale.width, event.clientX - bounds.left));
    if (Math.abs(currentX - gesture.startX) < 3 && !gesture.moved) return;
    if (!gesture.moved) event.currentTarget.setPointerCapture(event.pointerId);
    gesture.moved = true;
    gesture.currentX = currentX;
    event.preventDefault();
    if (gesture.mode === "pan") {
      const timeDelta = -(currentX - gesture.startX) / Math.max(Number.EPSILON, gesture.scale.pixelsPerMillisecond);
      setVisibleDomain(gesture.scale.domainStart + timeDelta, gesture.scale.domainEnd + timeDelta);
      setZoomSelection(null);
      setChartHover(null);
      setTimelineCursor(null);
      return;
    }
    setZoomSelection({ x0: Math.min(gesture.startX, currentX), x1: Math.max(gesture.startX, currentX) });
    setChartHover(null);
  };
  const finishTimelineZoom = (event: ReactPointerEvent<HTMLDivElement>, applySelection = true) => {
    const gesture = zoomGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (gesture.moved) {
      suppressChartClickRef.current = true;
      window.setTimeout(() => { suppressChartClickRef.current = false; }, 0);
      const x0 = Math.min(gesture.startX, gesture.currentX);
      const x1 = Math.max(gesture.startX, gesture.currentX);
      if (gesture.mode === "zoom" && applySelection && x1 - x0 >= 8) {
        setVisibleDomain(gesture.scale.fromX(x0), gesture.scale.fromX(x1));
      }
    }
    zoomGestureRef.current = null;
    setIsPanning(false);
    setZoomSelection(null);
    setTimelineCursor(null);
  };
  const chartRun = (params: unknown) => {
    const dataIndex = (params as { dataIndex?: number }).dataIndex;
    const run = dataIndex == null ? null : visible[dataIndex] ?? null;
    return run && (!replay || replay.visibleRunIds.has(run.id)) ? run : null;
  };
  const inspectChartRun = (params: unknown) => {
    const run = chartRun(params);
    if (!run) return;
    const event = (params as { event?: { offsetX?: number; offsetY?: number } }).event;
    setInspectedRunId((current) => current === run.id ? current : run.id);
    setChartHover({ runId: run.id, x: event?.offsetX ?? 12, y: event?.offsetY ?? 12 });
  };
  const selectChartRun = (params: unknown) => {
    if (suppressChartClickRef.current) return;
    const run = chartRun(params);
    if (run == null) return;
    setInspectedRunId(run.id);
    onSelectRun?.(run.id);
  };
  const contentHeight = ganttContentHeight(visible.length);
  return <div className="run-explorer" onPointerDownCapture={replay?.onInspect} onKeyDownCapture={replay?.onInspect}>
    <div className="run-explorer__toolbar">
      <div className="segmented" aria-label="Run view">
        <button className={view === "gantt" ? "selected" : ""} onClick={() => setView("gantt")}><BarChart3 size={14} /> Gantt</button>
        <button className={view === "table" ? "selected" : ""} onClick={() => setView("table")}><Table2 size={14} /> Table</button>
      </div>
      <div className="segmented run-scope-control" aria-label="Run scope">
        <button className={runScope === "focus" ? "selected" : ""} aria-pressed={runScope === "focus"} title="Completed runs under 2 minutes and routine smoke/diag/audit checks are collapsed. Active and failed runs always remain." onClick={() => { setRunScope("focus"); resetZoom(); }}>Focus <span>{focusRuns.length}</span></button>
        <button className={runScope === "all" ? "selected" : ""} aria-pressed={runScope === "all"} onClick={() => { setRunScope("all"); resetZoom(); }}>All runs <span>{filtered.length}</span></button>
      </div>
      <input value={search} onChange={(event) => { setSearch(event.target.value); resetZoom(); }} placeholder="Search runs or paths" aria-label="Search runs" />
      <select value={kind} onChange={(event) => { setKind(event.target.value); resetZoom(); }} aria-label="Filter run kind"><option value="all">All types</option><option value="training">Training</option><option value="evaluation">Evaluation</option><option value="diagnostic">Diagnostic</option></select>
      <select value={status} onChange={(event) => { setStatus(event.target.value); resetZoom(); }} aria-label="Filter run status"><option value="all">All status</option>{[...new Set(runs.map((run) => run.status))].map((value) => <option key={value}>{value}</option>)}</select>
      <div className="segmented range-control" aria-label="Time range">{(["24h", "7d", "all"] as const).map((value) => <button key={value} className={range === value ? "selected" : ""} onClick={() => { setRange(value); resetZoom(); }}>{value === "all" ? "All" : value}</button>)}</div>
    </div>
    <div className="run-explorer__meta">
      <span><strong>{replay ? visible.filter((run) => replay.visibleRunIds.has(run.id)).length : visible.length}</strong>{replay && !replay.complete ? ` / ${visible.length} revealed runs` : " runs"}</span>
      {runScope === "focus" && collapsedCount > 0 ? <button className="quick-run-summary" onClick={() => { setRunScope("all"); resetZoom(); }}>{collapsedCount} quick checks collapsed · show all</button> : null}
      <span className="run-state-legend"><i />{replay && !replay.complete ? "Replaying" : "Running"}</span>
      <div className="run-kind-legend">{runKinds.map(({ kind: value, label }) => <span key={value} className={`run-kind run-kind--${value}`}><i />{label}</span>)}</div>
      {view === "gantt" && (compressedTimeScale.breaks.length || !gapCompressionEnabled) ? <button
        className={`timeline-break-summary${gapCompressionEnabled ? "" : " is-disabled"}`}
        aria-label={gapCompressionEnabled ? "Disable gap compression" : "Enable gap compression"}
        aria-pressed={gapCompressionEnabled}
        title={gapCompressionEnabled ? "Show a linear timeline without compressed gaps" : "Restore automatic gap compression"}
        onClick={() => { setGapCompressionEnabled((enabled) => !enabled); setTimelineCursor(null); }}
      >{gapCompressionEnabled ? `${compressedTimeScale.breaks.length} empty gap${compressedTimeScale.breaks.length === 1 ? "" : "s"} compressed` : "Gap compression off"}</button> : null}
      <span className="zoom-copy"><ZoomIn size={14} /> drag to zoom · swipe/right-drag to pan · double-click to reset</span>
      <button className="zoom-reset" disabled={zoomWindow.start === 0 && zoomWindow.end === 100} onClick={resetZoom}><RotateCcw size={13} /> Reset zoom</button>
    </div>
    {inspectedRun ? (
      <div className="run-lens" aria-live="polite">
        <div className="run-lens__identity"><span className={`run-kind run-kind--${inspectedRun.kind}`}><i />{inspectedRun.kind}</span><OverflowReveal as="strong" value={runName(inspectedRun)} label="Run name" /><StatusPill tone={replayActive(inspectedRun) ? "live" : statusTone(inspectedRun.status)}>{replayActive(inspectedRun) ? "Replaying saved Run" : inspectedRun.status}</StatusPill><span className="semantic-provenance" title={inspectedRun.semanticProvenance.rationale ?? "Semantics were recorded by the Harness."}>{semanticLabel(inspectedRun)}</span>{inspectedRun.phase ? <code>phase · {inspectedRun.phase}</code> : null}</div>
        <div className="run-lens__facts">
          <span><small>Duration</small><strong>{formatDuration(displayedDuration(inspectedRun))}</strong></span>
          <span><small>Started</small><strong>{new Date(runStart(inspectedRun)).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</strong></span>
          <span><small>{replayActive(inspectedRun) ? "Replay time" : isRunningRun(inspectedRun) ? "Now" : "Ended"}</small><strong>{new Date(replayActive(inspectedRun) ? replay!.cursorTime : runEnd(inspectedRun, now)).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</strong></span>
          <span><small>{inspectedRun.primaryMetricName ?? "Metric"}</small><strong>{inspectedRun.primaryMetricValue == null ? "—" : inspectedRun.primaryMetricValue.toLocaleString("en-US")}</strong></span>
        </div>
        <OverflowReveal as="code" value={inspectedRun.path} kind="path" label="Run path" copyable focusable />
        {!readOnly ? <Link to="/runs/$runId" params={{ runId: inspectedRun.id }}>Open run →</Link> : null}
      </div>
    ) : (
      <div className="run-lens run-lens--empty"><strong>Hover or select a Run</strong><span>Details stay here until another Run is inspected.</span></div>
    )}
    <div ref={scrollRef} className="run-explorer__viewport">
    {!visible.length ? <EmptyState title={runs.length ? "No matching runs" : "No runs in this Topic"} detail={runs.length ? "Change the time, status, type, or text filters." : emptyDetail} /> : view === "gantt" ? (
      <div className="gantt-scroll"><div className="gantt-grid" style={{ height: contentHeight }}>
        <div className="gantt-labels" style={{ height: contentHeight }}>
          {visible.map((run) => <button key={run.id} className={[selectedRunId === run.id ? "selected" : "", inspectedRunId === run.id ? "inspected" : "", isRunningRun(run) || replayActive(run) ? "running" : "", replay && !replay.visibleRunIds.has(run.id) ? "is-replay-pending" : ""].filter(Boolean).join(" ")} data-replay-active={replayActive(run) || undefined} disabled={Boolean(replay && !replay.visibleRunIds.has(run.id))} aria-label={`${runName(run)}, ${run.path}`} onMouseEnter={() => setInspectedRunId(run.id)} onFocus={() => setInspectedRunId(run.id)} onClick={() => { setInspectedRunId(run.id); onSelectRun?.(run.id); }}><i className={`run-kind-dot run-kind-dot--${run.kind}`} /><OverflowReveal as="span" value={runName(run)} label="Run name" /><small>{replay && !replay.visibleRunIds.has(run.id) ? "—" : formatDuration(displayedDuration(run))}</small></button>)}
          <div className="gantt-sticky-label-footer" aria-hidden="true" />
        </div>
        <div
          ref={chartShellRef}
          className={`gantt-chart-shell${isPanning ? " is-panning" : ""}`}
          data-zoom-start={zoomWindow.start}
          data-zoom-end={zoomWindow.end}
          onPointerDown={handleTimelinePointerDown}
          onPointerMove={handleTimelinePointerMove}
          onPointerUp={finishTimelineZoom}
          onPointerCancel={(event) => finishTimelineZoom(event, false)}
          onContextMenu={(event) => event.preventDefault()}
          onWheel={handleTimelineTrackpadPan}
          onDoubleClick={(event) => { event.preventDefault(); resetZoom(); setZoomSelection(null); }}
          onMouseLeave={() => { setChartHover(null); setTimelineCursor(null); }}
        >
          {replay?.clock ? <RunReplayChart runs={visible} scale={timeScale} observedAt={now} clock={replay.clock}
            colors={replayColors} gridColor={colorPalette === "classic" ? isLight ? "rgba(35,55,78,.10)" : "rgba(150,170,200,.12)" : palette.borderMuted}
            onHover={inspectChartRun} onLeave={() => setChartHover(null)} onSelect={selectChartRun} />
            : <Chart option={option} style={{ height: contentHeight }} notMerge={!replay} onEvents={{ mouseover: inspectChartRun, mouseout: () => setChartHover(null), click: selectChartRun }} />}
          {replay && !replay.clock && !replay.complete && replay.cursorTime >= visibleDomain.start && replay.cursorTime <= visibleDomain.end ? <div className="gantt-replay-sweep" aria-hidden="true" style={{ left: timeScale.toX(replay.cursorTime) }}><span>REPLAY</span></div> : null}
          {replay && !replay.clock && !replay.complete ? visible.map((run, index) => replayActive(run) && replay.cursorTime >= visibleDomain.start && replay.cursorTime <= visibleDomain.end ? <i key={run.id} className="gantt-replay-head" aria-hidden="true" style={{ left: timeScale.toX(replay.cursorTime), top: index * GANTT_ROW_HEIGHT + GANTT_ROW_HEIGHT / 2, "--run-replay-color": kindColor(run.kind, palette, colorPalette) } as CSSProperties} /> : null) : null}
          {timeScale.breaks.map((gap) => <div
            key={`${gap.start}-${gap.end}`}
            className="gantt-time-break"
            role="img"
            aria-label={`Skipped ${formatDuration(gap.durationMs)} with no visible runs`}
            style={{ "--break-x": `${gap.x0}px`, "--break-width": `${gap.x1 - gap.x0}px` } as CSSProperties}
          />)}
          {zoomSelection ? <div className="gantt-zoom-selection" aria-hidden="true" style={{ "--selection-x": `${zoomSelection.x0}px`, "--selection-width": `${zoomSelection.x1 - zoomSelection.x0}px` } as CSSProperties} /> : null}
          {timelineCursor ? <div className="gantt-time-cursor" style={{ "--cursor-x": `${timelineCursor.x}px` } as CSSProperties} /> : null}
          <div className="gantt-sticky-time-axis" aria-label="Timeline time axis">
            {timelineTicks.map((tick) => <time key={`${tick.position}-${tick.time}`} className={`gantt-time-tick gantt-time-tick--${tick.alignment}`} dateTime={new Date(tick.time).toISOString()} style={{ "--tick-x": `${tick.position}px` } as CSSProperties}>{formatTimelineTick(tick.time)}</time>)}
            {timelineCursor ? timelineCursor.gap
              ? <span className={`gantt-cursor-time gantt-cursor-time--gap gantt-cursor-time--${timelineCursor.alignment}`} style={{ "--cursor-x": `${timelineCursor.x}px` } as CSSProperties}>Skipped {formatDuration(timelineCursor.gap.durationMs)}</span>
              : <time className={`gantt-cursor-time gantt-cursor-time--${timelineCursor.alignment}`} dateTime={new Date(timelineCursor.time).toISOString()} style={{ "--cursor-x": `${timelineCursor.x}px` } as CSSProperties}>{formatTimelineTime(timelineCursor.time)}</time>
            : null}
          </div>
          {hoveredRun && chartHover ? <div className="gantt-chart-tooltip" style={{ "--tip-x": `${chartHover.x + 12}px`, "--tip-y": `${chartHover.y + 12}px` } as CSSProperties}>
            <small className="gantt-chart-tooltip__experiment">{experimentById.get(hoveredRun.experimentId)?.name ?? hoveredRun.experimentId}</small>
            <strong>{runName(hoveredRun)}</strong>
            <span>{hoveredRun.kind} · {replayActive(hoveredRun) ? "Replaying" : hoveredRun.status} · {formatDuration(displayedDuration(hoveredRun))}</span>
            <small>{new Date(runStart(hoveredRun)).toLocaleString("en-US")} → {new Date(replayActive(hoveredRun) ? replay!.cursorTime : runEnd(hoveredRun, now)).toLocaleString("en-US")}</small>
          </div> : null}
        </div>
      </div></div>
    ) : (
      <div className="table-shell run-table-shell"><table className="data-table"><thead><tr><th>Run / path</th><th>Experiment</th><th>Type / phase</th><th>Semantics</th><th>Status</th><th>Progress</th><th>Updated</th><th /></tr></thead><tbody>
        {visible.filter((run) => !replay || replay.visibleRunIds.has(run.id)).map((run) => replay?.runDetails.get(run.id) ?? run).slice().reverse().map((run) => <tr key={run.id} className={selectedRunId === run.id ? "selected-row" : ""}>
          <td><button className="run-name-button" onMouseEnter={() => setInspectedRunId(run.id)} onFocus={() => setInspectedRunId(run.id)} onClick={() => { setInspectedRunId(run.id); onSelectRun?.(run.id); }}><OverflowReveal as="strong" value={runName(run)} label="Run name" /><OverflowReveal as="code" value={run.path} kind="path" label="Run path" copyable /></button></td>
          <td>{experimentById.get(run.experimentId)?.name ?? run.experimentId}</td><td><span className={`run-kind run-kind--${run.kind}`}><i />{run.kind}</span>{run.phase ? <small> · {run.phase}</small> : null}</td><td><span className="semantic-provenance" title={run.semanticProvenance.rationale ?? undefined}>{semanticLabel(run)}</span></td><td><StatusPill tone={replayActive(run) ? "live" : statusTone(run.status)}>{replayActive(run) ? "Replaying" : run.status}</StatusPill></td>
          <td title={progressUnitLabel(run)}>{progressValue(run)}</td><td>{new Date(run.updatedAt).toLocaleString("en-US")}</td><td>{!readOnly ? <Link to="/runs/$runId" params={{ runId: run.id }}>Open →</Link> : null}</td>
        </tr>)}
      </tbody></table></div>
    )}</div>
  </div>;
}
